import { useMemo, useState } from 'react';
import { useProjectStore } from '@/stores';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui';
import { CheckCircle2, Clock, AlertCircle, Calendar, X, ArrowLeft } from 'lucide-react';
import type { Task, Phase } from '@/types';
import { motion, AnimatePresence } from 'motion/react';
import { autoCalculateAllTaskStatuses } from '@/utils/taskStatus';
import type { GanttChartRef } from '@/components/features/GanttChart';

// 阶段名称映射
const phaseNames: Record<Phase, string> = {
  supplierSelection: 'Supplier Selection',
  stylingConcept: 'Styling Concept',
  detailedDesign: 'Detailed Design',
  toolingLine: 'Tooling & Line',
  productDevelopment: 'Product Development',
};

// 获取任务显示名称
const getTaskDisplayName = (task: Task): string => {
  const phase = phaseNames[task.phase] || task.phase;
  const parts = [phase];
  if (task.name && task.name.trim()) parts.push(task.name.trim());
  if (task.subTask && task.subTask.trim()) parts.push(task.subTask.trim());
  return parts.join(' - ');
};

// 格式化日期
const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
};

type CardType = 'completed' | 'inProgress' | 'delayed';

interface TaskCard {
  id: CardType;
  title: string;
  description: string;
  count: number;
  icon: React.ReactNode;
  iconBgColor: string;
  textColor: string;
}

export function ToDoList({ 
  ganttChartRef,
  ganttContainerRef
}: { 
  ganttChartRef?: React.RefObject<GanttChartRef>;
  ganttContainerRef?: React.RefObject<HTMLDivElement>;
}) {
  const { tasks, currentProject } = useProjectStore();
  const [selectedCard, setSelectedCard] = useState<CardType | null>(null);

  const taskStats = useMemo(() => {
    const tasksWithAutoStatus = autoCalculateAllTaskStatuses(tasks);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 正常完成的任务
    const completed = tasksWithAutoStatus.filter(t => t.status === 'completed');
    // 延期完成的任务 - 归入Completed但单独标记
    const delayedCompleted = tasksWithAutoStatus.filter(t => t.status === 'delayed-completed');
    // 将延期完成的任务也归入Completed
    const allCompleted = [...completed, ...delayedCompleted];

    const inProgress = tasksWithAutoStatus.filter(t => t.status === 'in-progress');

    // 延期任务包括：
    // 1. 状态为 'delayed' 的任务（用户手动标记为计划延期）
    // 2. 结束日期已过且未完成的任务（排除delayed-completed，因为已经归入completed）
    const delayed = tasksWithAutoStatus.filter(t => {
      const endDate = new Date(t.end);
      const isOverdue = endDate < today && t.status !== 'completed' && t.status !== 'delayed-completed';
      const isMarkedDelayed = t.status === 'delayed';
      return isOverdue || isMarkedDelayed;
    });

    return { completed: allCompleted, inProgress, delayed, delayedCompleted };
  }, [tasks]);

  const cards: TaskCard[] = [
    {
      id: 'completed',
      title: 'Completed',
      description: 'Finished tasks',
      count: taskStats.completed.length,
      icon: <CheckCircle2 className="w-6 h-6 text-white" />,
      iconBgColor: '#57e434',
      textColor: 'text-green-600',
    },
    {
      id: 'inProgress',
      title: 'In Progress',
      description: 'Active tasks',
      count: taskStats.inProgress.length,
      icon: <Clock className="w-6 h-6 text-white" />,
      iconBgColor: '#2a36e2',
      textColor: 'text-blue-600',
    },
    {
      id: 'delayed',
      title: 'Delayed',
      description: 'Behind schedule',
      count: taskStats.delayed.length,
      icon: <AlertCircle className="w-6 h-6 text-white" />,
      iconBgColor: '#f44245',
      textColor: 'text-red-600',
    },
  ];

  const getTasksForCard = (cardId: CardType): Task[] => {
    switch (cardId) {
      case 'completed':
        return taskStats.completed;
      case 'inProgress':
        return taskStats.inProgress;
      case 'delayed':
        return taskStats.delayed;
      default:
        return [];
    }
  };

  const handleTaskClick = (task: Task) => {
    if (ganttChartRef?.current) {
      ganttChartRef.current.scrollToTask(task.id);
    }
    if (ganttContainerRef?.current) {
      ganttContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!currentProject) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-gray-400">
          Select a project to view tasks
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full overflow-hidden">
      <CardHeader>
        <CardTitle className="flex items-center text-lg font-bold text-gray-800">
          <Calendar className="w-5 h-5 mr-2 text-gray-500" />
          Task Overview
        </CardTitle>
      </CardHeader>
      <CardContent className="relative h-[320px]">
        <div className="relative w-full h-full">
          {/* 卡片列表 */}
          <div className={`flex ${selectedCard ? 'justify-start pl-[10%]' : 'justify-center'} items-center h-full gap-4 transition-all duration-500`}>
            {cards.map((card) => (
              <motion.div
                key={card.id}
                layout
                onClick={() => !selectedCard && setSelectedCard(card.id)}
                className={`cursor-pointer ${selectedCard && selectedCard !== card.id ? 'hidden' : ''}`}
                initial={false}
                animate={{
                  width: selectedCard === card.id ? 200 : 180,
                  scale: selectedCard === card.id ? 1 : 1,
                }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              >
                <div
                  className="rounded-2xl border border-gray-200 bg-white shadow-lg p-3 sm:p-6 flex flex-col items-center justify-center min-h-[240px]"
                  style={{
                    boxShadow: `0 10px 40px -10px ${card.iconBgColor}40`,
                  }}
                >
                  {/* Icon */}
                  <div
                    className="flex items-center justify-center w-10 h-10 sm:w-14 sm:h-14 rounded-full mb-2 sm:mb-4"
                    style={{ backgroundColor: card.iconBgColor }}
                  >
                    {card.icon}
                  </div>

                  {/* Title */}
                  <div className="text-base sm:text-xl font-bold text-gray-800 mb-1 text-center leading-tight whitespace-nowrap">
                    {card.title}
                  </div>

                  {/* Description */}
                  <div className="text-xs sm:text-sm text-gray-500 mb-2 sm:mb-4 text-center whitespace-nowrap hidden sm:block">
                    {card.description}
                  </div>

                  {/* Count */}
                  <div
                    className="text-3xl sm:text-5xl font-extrabold leading-none"
                    style={{ color: card.iconBgColor }}
                  >
                    {card.count}
                  </div>

                  {/* 点击提示 */}
                  {!selectedCard && (
                    <div className="mt-2 sm:mt-4 text-xs text-gray-400 hidden sm:block">
                      Click to view details
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* 任务明细面板 */}
          <AnimatePresence>
            {selectedCard && (
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 50 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                className="absolute right-0 top-0 bottom-0 w-[55%] bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden"
              >
                {/* 头部 */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedCard(null)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <div>
                      <h3 className="font-bold text-gray-800">
                        {cards.find(c => c.id === selectedCard)?.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {getTasksForCard(selectedCard).length} tasks
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCard(null)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                {/* 任务列表 */}
                <div className="overflow-y-auto h-[calc(100%-72px)] p-4">
                  {getTasksForCard(selectedCard).length > 0 ? (
                    <div className="space-y-3">
                      {getTasksForCard(selectedCard).map((task) => (
                        <div
                          key={task.id}
                          onClick={() => handleTaskClick(task)}
                          className="p-3 rounded-lg border border-gray-100 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                        >
                          <div className="font-medium text-sm text-gray-800 mb-1">
                            {getTaskDisplayName(task)}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{formatDate(task.start)} - {formatDate(task.end)}</span>
                            <span className={`px-2 py-0.5 rounded-full ${
                              task.status === 'delayed-completed'
                                ? 'bg-orange-100 text-orange-600'
                                : task.status === 'completed'
                                ? 'bg-green-100 text-green-600'
                                : task.status === 'in-progress'
                                ? 'bg-blue-100 text-blue-600'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {task.status === 'delayed-completed' ? 'Delayed Completed' : task.status === 'completed' ? 'Done' : task.status === 'in-progress' ? 'In Progress' : 'Pending'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                      <CheckCircle2 className="w-12 h-12 mb-2 opacity-30" />
                      <p>No tasks in this category</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </CardContent>
    </Card>
  );
}
